import { Alert, Box, CircularProgress, Link, Snackbar, TextField, Typography } from '@mui/material';
import BaseModal from 'components/BaseModal';
import { useState, useEffect } from 'react';
import { useStyles } from './styles';
import CustomButton from 'components/CustomButton';
import PropTypes from 'prop-types';
import ActivationCodeService from 'services/ActivationCodeService';
import { KeyCodeTypeEnum } from 'enums';

const Props = {
  setValidCoupon: PropTypes.func.isRequired,
  pseudoElementBgColor: PropTypes.string,
  initialModalState: PropTypes.bool,
};

export default function ResearcherLifeCouponModal({
  setValidCoupon,
  pseudoElementBgColor,
  initialModalState = false,
  purpleBallonDesign = true,
  fromHeader = false,
}) {
  const classes = useStyles({ pseudoElementBgColor });
  const [modalState, setModalState] = useState({
    isOpen: initialModalState,
    isLoading: false,
    couponCode: '',
    errorMessage: '',
    snackBarOpen: false,
  });

  useEffect(() => {
    if (initialModalState) {
      setModalState((prev) => ({ ...prev, isOpen: true }));
    }
  }, [initialModalState]);

  if (!setValidCoupon) {
    throw new Error('setValidCoupon is required');
  }

  function handleModalClose() {
    setModalState({ ...modalState, isOpen: false });
  }

  function handleModalOpen() {
    setModalState({ ...modalState, isOpen: true });
  }

  function handleCouponChange(e) {
    const value = e?.target?.value ?? '';
    setModalState({ ...modalState, couponCode: value, errorMessage: '' });
  }

  function timeOutCloseSnackBar() {
    setTimeout(() => {
      setModalState({ ...modalState, snackBarOpen: false });
    }, 2000);
  }

  async function submitCouponCode() {
    try {
      setModalState({ ...modalState, isLoading: true });

      const { data } = await ActivationCodeService.applyCodeAtivationFroEditage(
        modalState.couponCode,
      );

      if (data?.validCoupon === true) {
        setValidCoupon(true);
        setModalState({
          isOpen: false,
          isLoading: false,
          couponCode: '',
          errorMessage: '',
          snackBarOpen: true,
        });

        return;
      }

      setModalState({ ...modalState, isLoading: false, errorMessage: data.message });
    } catch (error) {
      console.error('Error applying coupon:', error);
      setModalState({ ...modalState, isLoading: false });
    }
  }
  // Prevent the workspace global Ctrl/Cmd+V handler from running when
  // pasting into modal inputs. We don't prevent the paste itself.
  function stopGlobalPastePropagation(e) {
    const isPasteKey = (e.ctrlKey || e.metaKey) && e.keyCode === KeyCodeTypeEnum.V;
    if (isPasteKey) {
      e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }
  }

  function modalContent() {
    return (
      <Box px={8}>
        <Typography variant="h4" component="p" className={classes.title}>
          Enter activation code
        </Typography>

        <Typography variant="body1" component="p" className={classes.description}>
          Please enter the activation code provided by your university or institute.
        </Typography>

        <TextField
          variant="outlined"
          placeholder="Enter activation code"
          fullWidth
          value={modalState.couponCode}
          onChange={handleCouponChange}
          onKeyDown={stopGlobalPastePropagation}
          className={classes.input}
          InputProps={{
            classes: {
              input: classes.input,
            },
          }}
        />

        {modalState.errorMessage && (
          <Typography variant="body1" component="p" className={classes.errorMessage}>
            {modalState.errorMessage}
          </Typography>
        )}

        {modalState.errorMessage && (
          <CustomButton onClick={handleModalClose} style={{ minWidth: '100%', marginBottom: 24 }}>
            Close
          </CustomButton>
        )}

        {!modalState.errorMessage && (
          <CustomButton
            disabled={modalState.isLoading || !modalState.couponCode}
            onClick={submitCouponCode}
            style={{ minWidth: '100%', marginBottom: 24 }}
          >
            {modalState.isLoading && (
              <CircularProgress size={14} color="inherit" style={{ marginRight: 5 }} />
            )}
            Submit
          </CustomButton>
        )}
      </Box>
    );
  }

  return (
    <>
      <Box className={purpleBallonDesign ? classes.couponActivation : classes.codeButton}>
        <Link
          className={fromHeader ? classes.headerButton : classes.unstyledButton}
          onClick={handleModalOpen}
        >
          {fromHeader ? 'Use activation code' : 'Have an activation code?'}
        </Link>
      </Box>

      <BaseModal
        size="sm"
        open={modalState.isOpen}
        onClose={handleModalClose}
        content={modalContent()}
        actionButton={false}
        cancelButton={false}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={modalState.snackBarOpen}
        autoHideDuration={2000}
        onClose={timeOutCloseSnackBar}
      >
        <Alert severity="success">Coupon applied successfully! </Alert>
      </Snackbar>
    </>
  );
}

ResearcherLifeCouponModal.propTypes = Props;
